import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Add,
  Delete,
  Edit,
  Cancel as CancelIcon,
  CheckCircle,
  Schedule,
  Error,
  Videocam,
  Sync
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import * as scheduledMeetingService from '../../services/scheduledMeetingService';
import * as outlookCalendarService from '../../services/outlookCalendarService';

const Scheduler = () => {
  const [scheduledMeetings, setScheduledMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { user } = useAuth();
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');
  const [formDialog, setFormDialog] = useState({ open: false, meeting: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, meeting: null });
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    meetingUrl: '',
    platform: 'zoom',
    scheduledTime: '',
    duration: 60,
    useGuestMode: false,
    guestName: 'EchoNote'
  });

  useEffect(() => {
    document.title = 'Scheduler - EchoNote';
    loadScheduledMeetings();
    
    // Auto-refresh every 30 seconds to update bot status
    // Only continue refreshing if user is still logged in
    const interval = setInterval(() => {
      if (user) {
        loadScheduledMeetings();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const loadScheduledMeetings = async () => {
    try {
      const response = await scheduledMeetingService.getScheduledMeetings();
      setScheduledMeetings(response.data);
    } catch (error) {
      console.error('Failed to load scheduled meetings:', error);
      setError('Failed to load scheduled meetings');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenFormDialog = (meeting = null) => {
    if (meeting) {
      // Format the date for datetime-local input
      const scheduledTime = new Date(meeting.scheduledTime);
      const formattedTime = scheduledTime.toISOString().slice(0, 16);
      
      setFormData({
        title: meeting.title,
        description: meeting.description || '',
        meetingUrl: meeting.meetingUrl,
        platform: meeting.platform,
        scheduledTime: formattedTime,
        duration: meeting.duration,
        useGuestMode: meeting.useGuestMode || false,
        guestName: meeting.guestName || 'EchoNote'
      });
    } else {
      setFormData({
        title: '',
        description: '',
        meetingUrl: '',
        platform: 'zoom',
        scheduledTime: '',
        duration: 60,
        useGuestMode: false,
        guestName: 'EchoNote'
      });
    }
    setFormDialog({ open: true, meeting });
  };

  const handleCloseFormDialog = () => {
    setFormDialog({ open: false, meeting: null });
    setError('');
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async () => {
    try {
      setError('');
      
      if (!formData.title || !formData.meetingUrl || !formData.scheduledTime) {
        setError('Please fill in all required fields');
        return;
      }

      const data = {
        ...formData,
        scheduledTime: new Date(formData.scheduledTime).toISOString()
      };

      if (formDialog.meeting) {
        await scheduledMeetingService.updateScheduledMeeting(formDialog.meeting.id, data);
      } else {
        await scheduledMeetingService.createScheduledMeeting(data);
      }

      await loadScheduledMeetings();
      handleCloseFormDialog();
    } catch (error) {
      console.error('Submit error:', error);
      setError(error.response?.data?.error || 'Failed to save scheduled meeting');
    }
  };

  const handleCancel = async (meeting) => {
    try {
      await scheduledMeetingService.cancelScheduledMeeting(meeting.id);
      await loadScheduledMeetings();
    } catch (error) {
      console.error('Cancel error:', error);
      setError('Failed to cancel meeting');
    }
  };

  const handleDelete = async () => {
    try {
      await scheduledMeetingService.deleteScheduledMeeting(deleteDialog.meeting.id);
      setScheduledMeetings(scheduledMeetings.filter(m => m.id !== deleteDialog.meeting.id));
      setDeleteDialog({ open: false, meeting: null });
    } catch (error) {
      console.error('Delete error:', error);
      setError('Failed to delete scheduled meeting');
    }
  };

  const handleSyncOutlook = async () => {
    try {
      setSyncing(true);
      setError('');
      setSyncMessage('');
      
      const response = await outlookCalendarService.syncCalendar({ 
        daysAhead: 30,
        includePast: false 
      });
      
      const results = response.data.results;
      if (results.created > 0) {
        setSyncMessage(`Successfully synced ${results.created} meeting(s) from Outlook calendar!`);
      } else if (results.total > 0) {
        setSyncMessage('No new meetings to sync. All calendar events are already scheduled or don\'t have meeting links.');
      } else {
        setSyncMessage('No calendar events found in the next 30 days.');
      }
      
      await loadScheduledMeetings();
      setTimeout(() => setSyncMessage(''), 5000);
    } catch (error) {
      console.error('Sync error:', error);
      const errorMsg = error.response?.data?.details || error.response?.data?.error || 'Failed to sync calendar';
      setError(errorMsg);
    } finally {
      setSyncing(false);
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      scheduled: { color: 'default', icon: <Schedule />, label: 'Scheduled' },
      joining: { color: 'info', icon: <Videocam />, label: 'Joining' },
      recording: { color: 'warning', icon: <Videocam />, label: 'Recording' },
      completed: { color: 'success', icon: <CheckCircle />, label: 'Completed' },
      failed: { color: 'error', icon: <Error />, label: 'Failed' },
      cancelled: { color: 'default', icon: <CancelIcon />, label: 'Cancelled' }
    };

    const config = statusConfig[status] || statusConfig.scheduled;
    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color}
        size="small"
      />
    );
  };

  const columns = [
    {
      field: 'title',
      headerName: 'Title',
      width: 200,
      flex: 1
    },
    {
      field: 'platform',
      headerName: 'Platform',
      width: 100,
      renderCell: (params) => (
        <Chip 
          label={params.value.toUpperCase()} 
          size="small" 
          variant="outlined"
        />
      )
    },
    {
      field: 'scheduledTime',
      headerName: 'Scheduled Time',
      width: 180,
      renderCell: (params) => {
        const date = new Date(params.value);
        return date.toLocaleString();
      }
    },
    {
      field: 'duration',
      headerName: 'Duration',
      width: 100,
      renderCell: (params) => `${params.value} min`
    },
    {
      field: 'botStatus',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => getStatusChip(params.value)
    },
    {
      field: 'createdBy',
      headerName: 'Created By',
      width: 150,
      renderCell: (params) => 
        `${params.row.createdBy.firstName} ${params.row.createdBy.lastName}`
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          {params.row.botStatus === 'scheduled' && (
            <>
              <IconButton
                size="small"
                onClick={() => handleOpenFormDialog(params.row)}
                color="primary"
              >
                <Edit />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleCancel(params.row)}
                color="warning"
              >
                <CancelIcon />
              </IconButton>
            </>
          )}
          <IconButton
            size="small"
            onClick={() => setDeleteDialog({ open: true, meeting: params.row })}
            color="error"
          >
            <Delete />
          </IconButton>
        </Box>
      )
    }
  ];

  const stats = {
    total: scheduledMeetings.length,
    scheduled: scheduledMeetings.filter(m => m.botStatus === 'scheduled').length,
    active: scheduledMeetings.filter(m => ['joining', 'recording'].includes(m.botStatus)).length,
    completed: scheduledMeetings.filter(m => m.botStatus === 'completed').length
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Meeting Scheduler
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={syncing ? <CircularProgress size={20} color="inherit" /> : <Sync />}
            onClick={handleSyncOutlook}
            disabled={syncing}
          >
            {syncing ? 'Syncing...' : 'Sync Outlook Calendar'}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenFormDialog()}
          >
            Schedule Meeting
          </Button>
        </Box>
      </Box>

      {syncMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSyncMessage('')}>
          {syncMessage}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        The meeting bot will automatically join your scheduled meetings and record them for transcription.
      </Alert>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Scheduled
              </Typography>
              <Typography variant="h4">
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Upcoming
              </Typography>
              <Typography variant="h4" color="primary.main">
                {stats.scheduled}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active Now
              </Typography>
              <Typography variant="h4" color="warning.main">
                {stats.active}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Completed
              </Typography>
              <Typography variant="h4" color="success.main">
                {stats.completed}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Scheduled Meetings Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Scheduled Meetings
          </Typography>
          <Box sx={{ height: 400, width: '100%' }}>
            <DataGrid
              rows={scheduledMeetings}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10]}
              disableSelectionOnClick
            />
          </Box>
        </CardContent>
      </Card>

      {/* Form Dialog */}
      <Dialog open={formDialog.open} onClose={handleCloseFormDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {formDialog.meeting ? 'Edit Scheduled Meeting' : 'Schedule New Meeting'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Meeting Title"
              name="title"
              value={formData.title}
              onChange={handleFormChange}
              required
            />
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleFormChange}
              multiline
              rows={2}
            />
            <TextField
              fullWidth
              label="Meeting URL"
              name="meetingUrl"
              value={formData.meetingUrl}
              onChange={handleFormChange}
              required
              placeholder="https://zoom.us/j/... or https://teams.microsoft.com/..."
            />
            <FormControl fullWidth>
              <InputLabel>Platform</InputLabel>
              <Select
                name="platform"
                value={formData.platform}
                onChange={handleFormChange}
                label="Platform"
              >
                <MenuItem value="zoom">Zoom</MenuItem>
                <MenuItem value="teams">Microsoft Teams</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Scheduled Time"
              name="scheduledTime"
              type="datetime-local"
              value={formData.scheduledTime}
              onChange={handleFormChange}
              required
              InputLabelProps={{
                shrink: true,
              }}
            />
            <TextField
              fullWidth
              label="Duration (minutes)"
              name="duration"
              type="number"
              value={formData.duration}
              onChange={handleFormChange}
              inputProps={{ min: 15, max: 480 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="useGuestMode"
                  checked={formData.useGuestMode}
                  onChange={handleFormChange}
                />
              }
              label="Join as Guest (no authentication required)"
            />
            {formData.useGuestMode && (
              <TextField
                fullWidth
                label="Guest Display Name"
                name="guestName"
                value={formData.guestName}
                onChange={handleFormChange}
                placeholder="EchoNote"
                helperText="The name that will appear to other meeting participants"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFormDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {formDialog.meeting ? 'Update' : 'Schedule'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, meeting: null })}
      >
        <DialogTitle>Delete Scheduled Meeting</DialogTitle>
        <DialogContent>
          Are you sure you want to delete "{deleteDialog.meeting?.title}"? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, meeting: null })}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Scheduler;
